import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router';

type BlockChecker = () => boolean;
export class BlockerManager {
  _isEnabled: boolean = true;

  get isEnabled() {
    return this._isEnabled;
  }

  _blockCheckerSet = new Set<BlockChecker>();

  addBlockChecker(blockChecker: BlockChecker) {
    this._blockCheckerSet.add(blockChecker);
  }

  removeBlockChecker(blockChecker: BlockChecker) {
    this._blockCheckerSet.delete(blockChecker);
  }

  shouldBlockNavigation() {
    return (
      this._isEnabled &&
      this._blockCheckerSet.size > 0 &&
      [...this._blockCheckerSet].some(blockChecker => blockChecker())
    );
  }

  enable() {
    this._isEnabled = true;
  }

  disable() {
    this._isEnabled = false;
  }
}

let blockerManagerSingleton: BlockerManager | null = null;

/**
 * This hook activates the navigation blocker system. It should be used only once per route, so the "useNavigationGuardBlocker" is provided
 * so it can be used by children elements if needed.
 * The main intention of this component is to provide a way to know when the navigation has been blocked, so some visual cue can be presented,
 * and utilities to allow or deny the navigation request. It will also ensure that browser triggered reloads or closing of the window/tab
 * are caught and a system prompt requiring confirmation is displayed.
 *
 * As there might be need for components (children or parent) to force navigation and bypass the block, the "disableNavigationGuard"
 * function is also provided with the ability to execute some code (usually navigation related) without the blocking affecting it, 
 * without any harmful effect if "useNavigationGuard" isn't in the react tree.
 *
 * The best way to use this component is at a high level (closer to root) as possible so children only need to use the
 * "useNavigationGuardBlocker" or the "disableNavigationGuard" artifacts.
 */
const useNavigationGuard = () => {
  if (!blockerManagerSingleton) {
    blockerManagerSingleton = new BlockerManager();
  }
  const blockerManager = blockerManagerSingleton;

  const blocker = useBlocker(() => blockerManager.shouldBlockNavigation());

  const isBlocked = blocker.state === 'blocked';

  // Handle page refresh or tab close
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (blockerManager.shouldBlockNavigation()) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [blockerManager]);

  const confirmLeave = () => {
    blocker.proceed?.();
  };

  const cancelLeave = () => {
    blocker.reset?.();
  };

  return {
    isBlocked,
    confirmLeave,
    cancelLeave,
  };
};

/**
 * This hook is meant to be used by children component that have responsibility over forms and need to set the condition for the blocker.
 * Specially important when there might be multiple editable elements on a single page and the "useNavigationGuard" has to be used at a
 * higher (parent) level.
 *
 * Its intent is to ensure the referential equality of the blocker check functions for each hook usage, so it leverages that maintainment
 * out of the hook users and plain simple functions (different instances each render) can be passed down and still work properly.
 * */
export const useNavigationGuardBlocker = (blockChecker: BlockChecker) => {
  const innerBlockChecker = useRef<BlockChecker | null>(null);
  const subscribedBlockChecker = useRef<BlockChecker>(
    () => innerBlockChecker.current?.() ?? false,
  );

  innerBlockChecker.current = blockChecker;

  if (blockerManagerSingleton) {
    blockerManagerSingleton.addBlockChecker(subscribedBlockChecker.current);
  } else {
    console.warn(
      `"useNavigationGuardBlocker" hook is meant to be used on a deeper level than the "useNavigationGuard" hook. It won't take effect in other case.`,
    );
  }

  useEffect(() => {
    return () => {
      // The instance is guaranteed to not have changed
      blockerManagerSingleton?.removeBlockChecker(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        subscribedBlockChecker.current,
      );
    };
  }, []);
};

/**
 * This function provides a way to bypass some execution around the current navigation guard.
 * Specially meant to force navigation if needed:
 * ```ts
 * void disableNavigationGuard(() => navigate("/some/url"))
 * ```
 *
 * It ensures the navigation guard is enabled after the execution ends.
 * */
export function disableNavigationGuard(
  execute: () => Promise<unknown> | void,
): Promise<void> {
  blockerManagerSingleton?.disable();
  return Promise.resolve(execute())
    .catch(() => {
      return;
    })
    .then(() => {
      blockerManagerSingleton?.enable();
      return;
    });
}

export default useNavigationGuard;
